import * as React from "react";
import { cn } from "../../lib/utils";
import { Button } from "../button";
import { Separator } from "../separator";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../breadcrumb";

const PageHeader = React.forwardRef(
  (
    {
      className,
      title,
      description,
      breadcrumbs,
      actions,
      contextualHelp,
      help,
      helpDivider = true,
      children,
      ...props
    },
    ref,
  ) => {
    const rawHelp = contextualHelp ?? help;
    const hasActions = Boolean(
      React.isValidElement(actions) ||
        (Array.isArray(actions) && actions.length > 0),
    );
    const hasHelp = Boolean(rawHelp);

    const renderActionsContent = () => {
      if (React.isValidElement(actions)) {
        return actions;
      }
      if (Array.isArray(actions) && actions.length > 0) {
        return actions.map((action, index) => {
          const Icon = action.icon;
          return (
            <Button
              key={index}
              variant={action.variant || "default"}
              size={action.size || "default"}
              onClick={action.onClick}
            >
              {Icon && <Icon className="mr-2 h-4 w-4" aria-hidden="true" />}
              {action.label}
            </Button>
          );
        });
      }
      return null;
    };

    const renderHelpContent = () => {
      if (!rawHelp) return null;
      if (React.isValidElement(rawHelp)) {
        return rawHelp;
      }
      return null;
    };

    return (
      <div ref={ref} className={cn("space-y-4", className)} {...props}>
        {breadcrumbs && breadcrumbs.length > 0 && (
          <Breadcrumb>
            <BreadcrumbList>
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={index}>
                  <BreadcrumbItem>
                    {index === breadcrumbs.length - 1 ? (
                      <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink href={crumb.href}>
                        {crumb.label}
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                  {index < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
                </React.Fragment>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
        )}

        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1 min-w-0">
            {title && (
              <h1 className="text-2xl font-display font-semibold tracking-tight text-foreground">
                {title}
              </h1>
            )}
            {description && (
              <p className="text-sm text-muted-foreground">{description}</p>
            )}
          </div>
          {(hasActions || hasHelp) && (
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              {hasActions && renderActionsContent()}
              {hasActions && hasHelp && helpDivider && (
                <Separator
                  orientation="vertical"
                  className="h-5 w-[1px] bg-border mx-0.5 sm:mx-1 shrink-0"
                />
              )}
              {hasHelp && renderHelpContent()}
            </div>
          )}
        </div>
        {children}
      </div>
    );
  },
);
PageHeader.displayName = "PageHeader";

export { PageHeader };
